<template>
  <validation-observer v-slot="{ handleSubmit }" tag="form" disabled>
    <ui-modal
      v-bind="$attrs"
      v-on="$listeners"
      @before-open="beforeOpen"
      modal-classes="location-group-form mt-4"
    >
      <template #title>
        <div class="modal-title text-center">
          {{ $t('staffs.title') }}
        </div>
      </template>
      <template #footer>
        <div class="d-flex justify-content-center w-100 mt-4">
          <ui-button
            action
            type="primary"
            native-type="button"
            class="mx-2"
            @click="handleSubmit(apply)"
          >
            {{ $t('form.buttons.applyGroup') }}
          </ui-button>
        </div>
      </template>

      <div class="container">
        <div class="row">
          <div class="col-lg-8 col-md-8 col-sm-8 col-12 mt-lg-0 mt-4 px-xl-3 px-lg-3 px-md-3 px-sm-3 px-0">
            <validation-provider
              tag="div"
              :name="$t('form.labels.groupName')"
              rules="required"
              mode="passive"
              v-slot="{ errors }"
            >
              <ui-input
                v-model="form.name"
                :label="$t('form.labels.groupName')"
                required
                input-classes="w-100"
                :error="errors[0]"
              ></ui-input>
            </validation-provider>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-8 col-md-8 col-sm-8 col-12 px-xl-3 px-lg-3 px-md-3 px-sm-3 px-0">
            <validation-provider
              tag="div"
              :name="$t('form.labels.tipCollectionType')"
              rules="required"
              mode="passive"
              v-slot="{ errors }"
            >
              <ui-select
                v-model="form.groupTipsType"
                :items="tipsTypes"
                :label="$t('form.labels.tipCollectionType')"
                :placeholder="$t('form.placeholders.select')"
                label-key="label"
                value-key="value"
                required
                :error="errors[0]"
               />
            </validation-provider>
          </div>

          <div class="col-lg-8 col-md-8 col-sm-8 col-12 px-xl-3 px-lg-3 px-md-3 px-sm-3 px-0">
            <validation-provider
              v-if="!isNoPaymentGateway"
              tag="div"
              :name="$t('form.labels.groupDescription')"
              mode="passive"
              rules="min:1|max:30"
              v-slot="{ errors }"
            >
              <ui-input
                v-model="form.description"
                maxlength="30"
                minlength="1"
                :label="$t('form.labels.groupDescription')"
                input-classes="w-100"
                :error="errors[0]"
                :counter="30"
              />
            </validation-provider>
          </div>
          <div class="col-lg-8 col-md-8 col-sm-8 col-12 px-xl-3 px-lg-3 px-md-3 px-sm-3 px-0">
            <validation-provider
              v-if="!isNoPaymentGateway"
              tag="div"
              name="Pay buttons"
              rules="required"
              mode="passive"
              v-slot="{ errors }"
            >
              <ui-select
                v-model="form.payButtonValue"
                :items="rawPayButtons"
                label="Pay buttons"
                :placeholder="$t('form.placeholders.select')"
                label-key="displayName"
                value-key="id"
                value-original
                filterable
                required
                :error="errors[0]"
              />
            </validation-provider>
          </div>
        </div>

        <template v-if="location.enableServiceAreaLayout">
          <div class="location-group-form__separator" />

          <div class="location-group-form__service-area">
            <div class="location-group-form__subtitle">
              Let users enter the room they are staying in
            </div>

            <validation-provider
              tag="div"
              name="Input field settings"
              rules="required"
              mode="passive"
              v-slot="{ errors }"
            >
              <ui-select
                v-model="form.serviceAreaState"
                :items="GROUP_SERVICE_AREA_STATE_SELECT_ITEMS"
                label="Input field settings"
                :placeholder="$t('form.placeholders.select')"
                filterable
                required
                :error="errors[0]"
              />
            </validation-provider>

            <template v-if="form.serviceAreaState === GROUP_SERVICE_AREA_STATE.CUSTOM">
              <validation-provider
                tag="div"
                :name="$t('form.labels.placeholderText')"
                mode="passive"
                rules="required|min:1|max:30"
                v-slot="{ errors }"
              >
                <ui-input
                  v-model="form.serviceAreaPlaceholderText"
                  maxlength="30"
                  minlength="1"
                  required
                  :label="$t('form.labels.placeholderText')"
                  :error="errors[0]"
                  :counter="30"
                />
              </validation-provider>

              <validation-provider
                tag="div"
                :name="$t('form.labels.inputFieldText')"
                mode="passive"
                rules="required|min:1|max:5"
                v-slot="{ errors }"
              >
                <ui-input
                  v-model="form.serviceAreaInputFieldText"
                  maxlength="5"
                  minlength="1"
                  required
                  :label="$t('form.labels.inputFieldText')"
                  :error="errors[0]"
                  :counter="5"
                />
              </validation-provider>

              <validation-provider
                tag="div"
                :name="$t('form.labels.requiredToFillIn')"
                mode="passive"
                rules="required"
                v-slot="{ errors }"
              >
                <ui-select
                  v-model="form.isServiceAreaRequired"
                  :items="REQUIRED_SELECTOR_ITEMS"
                  :label="$t('form.labels.requiredToFillIn')"
                  :label-info="$t('serviceAreaRequiredSelect.tooltip')"
                  :placeholder="$t('form.placeholders.select')"
                  required
                  :error="errors[0]"
                />
              </validation-provider>
            </template>
          </div>
        </template>

        <div class="row">
          <div class="col-12 mt-lg-0 mt-4 px-xl-3 px-lg-3 px-md-3 px-sm-3 px-0">
            <validation-provider
              tag="div"
              rules="required"
              mode="passive"
              v-slot="{ errors }"
            >
              <location-service-type
                v-model="activeType"
                :type-list="groupTypeList"
                :label="$t('staffs.selectIcon')"
                :error="errors[0]"
                class="is-filled"
                @toggle-icon="toggleIcon"
              />
            </validation-provider>
          </div>
        </div>
        <div class="row">
          <div class="col-12 mt-lg-0 mt-4 px-xl-3 px-lg-3 px-md-3 px-sm-3 px-0">
            <ui-loading v-if="loading.image" inline />
            <location-service-image
              v-else
              v-model="form.image"
              label="Select or upload image for group"
              class="is-filled"
            />
          </div>
        </div>
      </div>
    </ui-modal>
  </validation-observer>
</template>

<script>
import { mapState } from 'vuex';
import ResetMixin from '@/mixins/reset-mixin';
import LocationServiceType from '@/components/Location/LocationServiceType.vue';
import LocationServiceImage from '@/components/Location/LocationServiceImage.vue';
import api from '@/api';
import { GROUP_SERVICE_AREA_STATE } from '@/config/groupServiceAreaState';
import { REQUIRED_SELECTOR_ITEMS } from '@/config/requiredSelectorItems';
import { PROCESSING_METHOD } from '@/config/processingMethod';

const GROUP_SERVICE_AREA_STATE_SELECT_ITEMS = [
  {
    name: 'Default',
    value: GROUP_SERVICE_AREA_STATE.DEFAULT,
  },
  {
    name: 'Enable (Custom)',
    value: GROUP_SERVICE_AREA_STATE.CUSTOM,
  },
  {
    name: 'Disabled',
    value: GROUP_SERVICE_AREA_STATE.DISABLED,
  },
];

export default {
  components: {
    LocationServiceType,
    LocationServiceImage,
  },

  mixins: [
    ResetMixin(() => ({
      form: {
        name: null,
        groupTipsType: null,
        icon: null,
        image: null,
        hasImage: false,
        description: null,
        payButtonValue: null,
        serviceAreaState: GROUP_SERVICE_AREA_STATE.SAME_AS_LOCATION,
        serviceAreaPlaceholderText: '',
        serviceAreaInputFieldText: '',
        isServiceAreaRequired: false,
      },
      loading: {
        image: false,
      },
    })),
  ],

  model: {
    prop: 'show',
    event: 'input',
  },

  props: {
    group: Object,
    location: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      GROUP_SERVICE_AREA_STATE,
      REQUIRED_SELECTOR_ITEMS,
      GROUP_SERVICE_AREA_STATE_SELECT_ITEMS,
      groupTypeList: [
        {
          name: 'ATTENDANT',
          icon: require('@/assets/images/groups/spa-attendant.svg'), // eslint-disable-line global-require
          label: this.$t('staffGroups.ATTENDANT'),
        },
        {
          name: 'BAR',
          icon: require('@/assets/images/groups/spa-bar.svg'), // eslint-disable-line global-require
          label: this.$t('staffGroups.BAR'),
        },
        {
          name: 'BEAUTICIAN',
          icon: require('@/assets/images/groups/spa-beautician.svg'), // eslint-disable-line global-require
          label: this.$t('staffGroups.BEAUTICIAN'),
        },
        {
          name: 'CAFE',
          icon: require('@/assets/images/groups/spa-cafe.svg'), // eslint-disable-line global-require
          label: this.$t('staffGroups.CAFE'),
        },
        {
          name: 'FITNESS',
          icon: require('@/assets/images/groups/spa-fitness.svg'), // eslint-disable-line global-require
          label: this.$t('staffGroups.FITNESS'),
        },
        {
          name: 'NAILS',
          icon: require('@/assets/images/groups/spa-nails.svg'), // eslint-disable-line global-require
          label: this.$t('staffGroups.NAILS'),
        },
        {
          name: 'RECEPTION',
          icon: require('@/assets/images/groups/spa-reception.svg'), // eslint-disable-line global-require
          label: this.$t('staffGroups.RECEPTION'),
        },
        {
          name: 'SALON',
          icon: require('@/assets/images/groups/spa-salon.svg'), // eslint-disable-line global-require
          label: this.$t('staffGroups.SALON'),
        },
        {
          name: 'SAUNA',
          icon: require('@/assets/images/groups/spa-sauna.svg'), // eslint-disable-line global-require
          label: this.$t('staffGroups.SAUNA'),
        },
        {
          name: 'YOGA',
          icon: require('@/assets/images/groups/spa-yoga.svg'), // eslint-disable-line global-require
          label: this.$t('staffGroups.YOGA'),
        },
        {
          name: 'WELLNESS',
          icon: require('@/assets/images/groups/spa-wellness.svg'), // eslint-disable-line global-require
          label: this.$t('staffGroups.WELLNESS'),
        },
      ],
    };
  },

  computed: {
    ...mapState('settings', [
      'rawPayButtons',
    ]),
    defaultPaymentButton() {
      return this.rawPayButtons.find((item) => !item.amountList.length);
    },
    activeType() {
      return this.form.icon?.name;
    },
    isNoPaymentGateway() {
      return this.location.processingMethod === PROCESSING_METHOD.NO_PAYMENT_GATEWAY;
    },
    tipsTypes() {
      return [
        {
          value: 'PERSONAL',
          label: 'Individual',
          visible: !this.isNoPaymentGateway,
        },
        {
          value: 'TIP_POOLING',
          label: 'Tip pooling',
          visible: true,
        },
        {
          value: 'TIP_SHARING',
          label: 'Tip sharing',
          visible: this.location.isTipOutEnabled,
        },
      ].filter((item) => item.visible);
    },
  },

  methods: {
    async beforeOpen() {
      this.reset();

      if (this.group) {
        this.form = {
          ...this.form,
          ...this.group,
          hasImage: this.group.hasImage,
          image: this.group.image,
        };

        if (!this.form.image && !this.form.hasImage) {
          this.loading.image = true;

          try {
            const { data } = await api.locations.getGroupImage({
              locationId: this.location.id,
              groupId: this.group.id,
            });
            this.form.image = data;
            this.form.hasImage = true;
          } catch (e) {
            this.form.hasImage = false;
          } finally {
            this.loading.image = false;
          }
        }
      } else {
        this.form.payButtonValue = this.defaultPaymentButton;
      }
    },
    apply() {
      this.$emit('update:group', this.form);
    },
    toggleIcon(name) {
      this.form.icon = { name };
    },
  },
};
</script>

<style lang="scss">
.location-group-form {
  &__separator {
    width: 100%;
    height: 1px;
    background-color: #D9D9D9;
    margin-bottom: 24px;
  }

  &__subtitle {
    font-size: 14px;
    line-height: 17px;
    font-weight: 700;
    color: #555555;
  }

  &__service-area {
    display: flex;
    flex-direction: column;
  }
}
</style>
